import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, ActivityIndicator } from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { apiGet, apiPost } from '../../../src/api/client';
import Card from '../../../src/components/Card';
import Button from '../../../src/components/Button';
import Input from '../../../src/components/Input';
import Picker from '../../../src/components/Picker';
import { useToast } from '../../../src/components/Toast';
import ContentContainer from '../../../src/components/ContentContainer';
import { useTheme } from '../../../src/context/ThemeContext';
import type { ColorTheme } from '../../../src/utils/theme';
import { spacing, fontSize, fontWeight } from '../../../src/utils/theme';

interface PricingTier {
  id: string;
  tierName: string;
}

/** AddFleetAccountScreen - form to create a new fleet customer account. */
export default function AddFleetAccountScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [tiers, setTiers] = useState<PricingTier[]>([]);

  // Form state
  const [companyName, setCompanyName] = useState('');
  const [billingEmail, setBillingEmail] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [paymentTermsDays, setPaymentTermsDays] = useState('15');
  const [taxId, setTaxId] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [pricingTierId, setPricingTierId] = useState('');
  const [invoiceAutoGeneration, setInvoiceAutoGeneration] = useState('off');
  const [autoGenFrequency, setAutoGenFrequency] = useState('monthly');
  const [autoGenDayOfWeek, setAutoGenDayOfWeek] = useState('1');

  const router = useRouter();
  const toast = useToast();

  useEffect(() => {
    const ac = new AbortController();
    fetchTiers(ac.signal);
    return () => ac.abort();
  }, []);

  const fetchTiers = async (signal?: AbortSignal) => {
    try {
      const data = await apiGet<PricingTier[]>('/api/fleet/pricing-tiers');
      if (signal?.aborted) return;
      setTiers(data);
    } catch (err: any) {
      if (!(signal?.aborted)) toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      if (!(signal?.aborted)) setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!companyName.trim()) {
      toast.show({ type: 'error', title: 'Error', message: 'Company name is required' });
      return;
    }
    if (!billingEmail.trim()) {
      toast.show({ type: 'error', title: 'Error', message: 'Billing email is required' });
      return;
    }
    try {
      setSubmitting(true);
      const daysNum = Number.parseInt(paymentTermsDays || '15', 10);
      if (Number.isNaN(daysNum) || daysNum < 0 || daysNum > 365) {
        toast.show({ type: 'error', title: 'Error', message: 'Net days must be between 0 and 365' });
        setSubmitting(false);
        return;
      }
      const body: any = {
        companyName,
        billingEmail,
        billingAddress: address,
        billingCity: city,
        billingState: state,
        billingZip: zip,
        paymentTermsDays: daysNum,
        taxId: taxId || undefined,
        primaryContactName: contactName || undefined,
        primaryContactPhone: contactPhone || undefined,
        primaryContactEmail: contactEmail || undefined,
      };

      if (pricingTierId) {
        body.pricingTierId = pricingTierId;
      }
      if (invoiceAutoGeneration !== 'off') {
        body.invoiceAutoGeneration = invoiceAutoGeneration;
        body.autoGenFrequency = autoGenFrequency;
        if (autoGenFrequency === 'weekly' || autoGenFrequency === 'biweekly') {
          body.autoGenDayOfWeek = parseInt(autoGenDayOfWeek, 10);
        }
      }

      await apiPost('/api/fleet/accounts', body);
      toast.show({ type: 'success', title: 'Success', message: 'Fleet account created' });
      router.back();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  const tierOptions = tiers.map(t => ({ label: t.tierName, value: t.id }));

  return (
    <ScrollView style={styles.container}>
      <ContentContainer>
      {/* Form */}
      <Card>
        <Text style={styles.sectionTitle}>Company Information</Text>

        <Input
          label="Company Name *"
          value={companyName}
          onChangeText={setCompanyName}
          placeholder="Enter company name"
        />

        <Input
          label="Billing Email *"
          value={billingEmail}
          onChangeText={setBillingEmail}
          placeholder="billing@company.com"
          keyboardType="email-address"
          autoCapitalize="none"
        />

        <Text style={[styles.sectionTitle, { marginTop: spacing.lg }]}>Address</Text>

        <Input
          label="Street Address"
          value={address}
          onChangeText={setAddress}
          placeholder="123 Main St"
        />

        <Input
          label="City"
          value={city}
          onChangeText={setCity}
          placeholder="City"
        />

        <View style={styles.row}>
          <View style={{ flex: 1 }}>
            <Input
              label="State"
              value={state}
              onChangeText={setState}
              placeholder="ST"
              maxLength={2}
              autoCapitalize="characters"
            />
          </View>
          <View style={{ flex: 1 }}>
            <Input
              label="ZIP Code"
              value={zip}
              onChangeText={setZip}
              placeholder="12345"
              keyboardType="numeric"
              maxLength={10}
            />
          </View>
        </View>

        <Text style={[styles.sectionTitle, { marginTop: spacing.lg }]}>Billing Details</Text>

        <Input
          label="Net days until due"
          value={paymentTermsDays}
          onChangeText={setPaymentTermsDays}
          placeholder="15"
          keyboardType="numeric"
          maxLength={3}
        />

        {tierOptions.length > 0 && (
          <Picker
            label="Pricing Tier"
            value={pricingTierId}
            onValueChange={setPricingTierId}
            options={[{ label: 'None', value: '' }, ...tierOptions]}
          />
        )}

        <Picker
          label="Auto Invoicing"
          value={invoiceAutoGeneration}
          onValueChange={setInvoiceAutoGeneration}
          options={[
            { label: 'Off', value: 'off' },
            { label: 'Generate & Notify Me', value: 'generate_notify' },
            { label: 'Generate & Auto-Send', value: 'generate_send' },
          ]}
        />

        {invoiceAutoGeneration !== 'off' && (
          <>
            <Picker
              label="Frequency"
              value={autoGenFrequency}
              onValueChange={setAutoGenFrequency}
              options={[
                { label: 'Weekly (prior 7 days)', value: 'weekly' },
                { label: 'Biweekly (prior 14 days)', value: 'biweekly' },
                { label: 'Monthly (1st of month, prior full month)', value: 'monthly' },
              ]}
            />
            {(autoGenFrequency === 'weekly' || autoGenFrequency === 'biweekly') && (
              <Picker
                label="Run Day"
                value={autoGenDayOfWeek}
                onValueChange={setAutoGenDayOfWeek}
                options={[
                  { label: 'Sunday', value: '0' },
                  { label: 'Monday', value: '1' },
                  { label: 'Tuesday', value: '2' },
                  { label: 'Wednesday', value: '3' },
                  { label: 'Thursday', value: '4' },
                  { label: 'Friday', value: '5' },
                  { label: 'Saturday', value: '6' },
                ]}
              />
            )}
          </>
        )}

        <Input
          label="Tax ID / EIN"
          value={taxId}
          onChangeText={setTaxId}
          placeholder="12-3456789"
        />

        <Text style={[styles.sectionTitle, { marginTop: spacing.lg }]}>Fleet Manager Contact</Text>

        <Input
          label="Fleet Manager Name"
          value={contactName}
          onChangeText={setContactName}
          placeholder="John Doe"
        />

        <Input
          label="Fleet Manager Phone"
          value={contactPhone}
          onChangeText={setContactPhone}
          placeholder="(555) 123-4567"
          keyboardType="phone-pad"
        />

        <Input
          label="Fleet Manager Email"
          value={contactEmail}
          onChangeText={setContactEmail}
          placeholder="john@company.com"
          keyboardType="email-address"
          autoCapitalize="none"
        />

        <View style={styles.actions}>
          <Button
            title="Cancel"
            onPress={() => router.back()}
            variant="outline"
            style={{ flex: 1 }}
          />
          <Button
            title="Create Account"
            onPress={handleSubmit}
            loading={submitting}
            style={{ flex: 1 }}
          />
        </View>
      </Card>

      <View style={{ height: spacing.xxl }} />
      </ContentContainer>
    </ScrollView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background, padding: spacing.md },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: c.background },

  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  headerTitle: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text },

  sectionTitle: {
    fontSize: fontSize.md,
    fontWeight: fontWeight.semibold,
    color: c.text,
    marginBottom: spacing.md,
  },

  row: { flexDirection: 'row', gap: spacing.sm },

  actions: { flexDirection: 'row', gap: spacing.md, marginTop: spacing.lg },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
